import { Injectable } from '@angular/core';

/**
 * Web Notifications for connect's agent/DM listener (MessagingListenerService).
 *
 * Reads the LIVE `Notification.permission` on every call: the user may grant
 * it through the Account-settings toggle or the notifications nudge, neither of
 * which goes through this service — prod 2026-09-23 the cached copy taken at
 * boot stayed 'default' and every agent answer produced the sound but no
 * notification. A gesture-less `requestPermission()` (boot) is attempted once;
 * Chrome answers it with the quiet prompt, so the nudge is what actually gets
 * the permission granted.
 */
@Injectable({
  providedIn: 'root'
})
export class BrowserNotificationService {

  private requested = false;

  public get supported(): boolean {
    return typeof Notification !== 'undefined';
  }

  public get permission(): NotificationPermission {
    return this.supported ? Notification.permission : 'denied';
  }

  public async requestPermission(): Promise<NotificationPermission> {
    if (!this.supported) {
      return 'denied';
    }
    if (Notification.permission !== 'default') {
      return Notification.permission;
    }
    this.requested = true;
    try {
      return await Notification.requestPermission();
    } catch {
      // Insecure context / embedded webview: equivalent to unavailable.
      return Notification.permission;
    }
  }

  public async showNotification(title: string, options: NotificationOptions): Promise<Notification | void> {
    if (!this.supported) {
      return;
    }
    if (Notification.permission === 'default' && !this.requested) {
      await this.requestPermission();
    }
    if (Notification.permission !== 'granted') {
      return;
    }
    try {
      return new Notification(title, options);
    } catch {
      return;
    }
  }
}
